import type { AnalysisOption, AnalysisType } from '../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import type { PlanFeatures } from '../../shared/types';
import { analysisTileAccess, creditCountLabel } from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';
import { tintSlot } from '../utils/tints';

interface Props {
  options: AnalysisOption[];
  onSelect: (option: AnalysisOption) => void;
  onUpgrade?: () => void;
  onUnitPurchase?: () => void;
}

// `adminOnly` hides the option entirely from non-super-admins. Used for
// features that exist in code but are not finished enough to ship publicly,
// even to paying tiers — e.g. Évin Créa is being kept admin-only until ready.
const featureMap: Record<string, { feature: keyof PlanFeatures; requiredPlan: 'Pro' | 'Premium'; adminOnly?: boolean }> = {
  brand_name: { feature: 'analysesText', requiredPlan: 'Pro' },
  slogan: { feature: 'analysesText', requiredPlan: 'Pro' },
  concept: { feature: 'analysesText', requiredPlan: 'Pro' },
  general_question: { feature: 'analysesText', requiredPlan: 'Pro' },
  label: { feature: 'analysesLabel', requiredPlan: 'Pro' },
  campaign: { feature: 'analysesCampaign', requiredPlan: 'Premium' },
  social_post: { feature: 'analysesSocial', requiredPlan: 'Premium' },
  creative_brief: { feature: 'analysesCreative', requiredPlan: 'Premium', adminOnly: true },
  event_activation: { feature: 'analysesEvent', requiredPlan: 'Premium' },
};

/* Mode pastel — position du curseur écrite en variables CSS sur la tuile
 * survolée : le halo radial de `.glow-card::after` (index.css, MODE PASTEL)
 * se recentre en continu sous la souris. Écriture directe dans le style de
 * l'élément — aucun re-render React, aucun effet dans les autres thèmes. */
function trackPointer(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
  el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
}
function resetPointer(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  el.style.removeProperty('--mx');
  el.style.removeProperty('--my');
}

export default function AnalysisTypeSelector({ options, onSelect, onUpgrade, onUnitPurchase }: Props) {
  const { features, organization, user, previewPlan } = useAuth();

  const isBeta = organization?.plan === 'beta';
  // Treat the admin as a regular user when they're previewing another plan,
  // so the preview reflects exactly what that plan would see.
  const isSuperAdmin = user?.isSuperAdmin === true && previewPlan === null;

  const visibleOptions = options.filter((o) => {
    // Évin Créa est masqué pour tous (feature mise en pause, non développée pour
    // l'instant). Le bloc « sur mesure » ci-dessous le remplace dans la grille.
    if (o.key === 'creative_brief') return false;
    const entry = featureMap[o.key];
    return entry?.adminOnly ? isSuperAdmin : true;
  });

  // Crédits achetés utilisables par type (org_credits, même règle que la
  // consommation) — débloquent une tuile même quand le plan ne couvre pas le
  // type (requireAnalysisAccess Path 3).
  const creditsFor = (key: AnalysisType) => organization?.creditsPerType?.[key] ?? 0;

  // Analyses offertes restantes : seuls les plans free et beta en ont (Path 2).
  const quotaRemaining = organization && (organization.plan === 'free' || organization.plan === 'beta')
    ? Math.max(0, (organization.freeAnalysesLimit ?? 0) - (organization.freeAnalysesUsed ?? 0))
    : 0;

  // État de chaque tuile, dans l'ordre où le serveur décide de l'accès
  // (shared/types.ts#analysisTileAccess, testée). `no_credit` : le plan couvre
  // le type mais il ne reste ni quota ni crédit (ex. compte sans abonnement
  // dont les crédits ne paient pas ce type, ou qui les a épuisés) ; la tuile
  // mène alors à l'achat au lieu d'échouer en 402.
  const accessFor = (key: AnalysisType) =>
    analysisTileAccess({ type: key, features, credits: creditsFor(key), quotaRemaining });

  // Verrouillé si rien ne paie l'analyse (plan, quota ou crédit).
  const isLocked = (key: AnalysisType) => {
    const kind = accessFor(key).kind;
    return kind === 'locked' || kind === 'no_credit';
  };

  // Total des crédits achetés restants (somme des lignes, pas des tuiles : un
  // crédit du Pack Test apparaît sur quatre tuiles mais ne paie qu'une analyse).
  const creditTotal = organization?.creditBalance ?? 0;

  const requiredPlanFor = (key: AnalysisType) => featureMap[key]?.requiredPlan ?? 'Pro';

  // Free / beta tier: show remaining analyses.
  // Un acheteur de crédits/bundle (crédits dispo, pas d'allocation gratuite)
  // n'est PAS concerné par ce bandeau « free » — il a déjà payé ; on l'affiche
  // donc seulement s'il existe une allocation gratuite (ou plan beta).
  const freeLimit = organization?.freeAnalysesLimit ?? 0;
  const hasCredits = (organization?.creditBalance ?? 0) > 0;
  const freeRemaining = (!features.unlimited && organization && !(hasCredits && freeLimit === 0))
    ? Math.max(0, freeLimit - (organization.freeAnalysesUsed ?? 0))
    : null;

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-10 pastel:text-left pastel:mb-9">
        <h2 className="font-display text-2xl pastel:text-[2.15rem] pastel:leading-[1.1] sm:pastel:text-[2.6rem] text-slate-900 dark:text-white mb-2">
          Que souhaitez-vous analyser ?
        </h2>
        <p className="text-slate-500 dark:text-slate-400 shader:text-white/50 max-w-xl mx-auto pastel:mx-0">
          &Eacute;valuez votre contenu et obtenez un score de conformit&eacute; avec la Loi &Eacute;vin.
        </p>
        {freeRemaining !== null && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center pastel:items-start pastel:justify-start sm:pastel:items-center gap-2 sm:gap-3">
            <span className={`text-sm font-semibold ${freeRemaining > 0 ? (isBeta ? 'text-emerald-600 dark:text-emerald-400 shader:text-emerald-300' : 'text-amber-600 dark:text-amber-400 shader:text-amber-300') : 'text-red-600 dark:text-red-400 shader:text-red-300'}`}>
              {freeRemaining > 0
                ? isBeta
                  ? `Il vous reste ${freeRemaining} analyse${freeRemaining > 1 ? 's' : ''} offerte${freeRemaining > 1 ? 's' : ''}`
                  : `${freeRemaining} analyse${freeRemaining > 1 ? 's' : ''} gratuite${freeRemaining > 1 ? 's' : ''} restante${freeRemaining > 1 ? 's' : ''} ce mois-ci`
                : isBeta
                  ? 'Vous avez utilisé toutes vos analyses offertes'
                  : freeLimit === 0
                    ? 'Aucune analyse incluse — abonnez-vous ou achetez un bundle'
                    : 'Vous avez utilisé vos analyses gratuites ce mois-ci'}
            </span>
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-1.5 px-3 py-1 pastel:px-3.5 pastel:py-1.5 text-xs font-semibold rounded-full border border-violet-300 dark:border-violet-500 shader:border-violet-400/40 pastel:border-transparent text-violet-700 dark:text-violet-300 shader:text-violet-200 pastel:text-white bg-violet-50 dark:bg-violet-900/30 shader:bg-violet-500/10 pastel:bg-(--p-ink) hover:bg-violet-100 dark:hover:bg-violet-900/50 shader:hover:bg-violet-500/20 pastel:hover:bg-(--p-ink) transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
              </svg>
              {isBeta ? 'Analyses offertes' : 'Offre gratuite'}
            </button>
          </div>
        )}

        {/* Récapitulatif des crédits achetés : le détail par type est sur
            chaque tuile, car un crédit ne paie que certains types. */}
        {creditTotal > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center pastel:items-start pastel:justify-start sm:pastel:items-center gap-2 sm:gap-3">
            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 shader:text-emerald-300">
              {creditCountLabel(creditTotal)} disponible{creditTotal > 1 ? 's' : ''}
            </span>
            <button
              onClick={onUnitPurchase ?? onUpgrade}
              className="inline-flex items-center gap-1.5 px-3 py-1 pastel:px-3.5 pastel:py-1.5 text-xs font-semibold rounded-full border border-violet-300 dark:border-violet-500 shader:border-violet-400/40 pastel:border-transparent text-violet-700 dark:text-violet-300 shader:text-violet-200 pastel:text-white bg-violet-50 dark:bg-violet-900/30 shader:bg-violet-500/10 pastel:bg-(--p-ink) hover:bg-violet-100 dark:hover:bg-violet-900/50 shader:hover:bg-violet-500/20 pastel:hover:bg-(--p-ink) transition-colors"
            >
              Acheter des crédits
            </button>
          </div>
        )}

        {/* À la carte hint — contextual, only for free users (not beta) */}
        {freeRemaining !== null && !isBeta && (
          freeRemaining > 0 ? (
            <p className="mt-3 text-sm text-slate-400 dark:text-slate-500 shader:text-white/30">
              Besoin ponctuel ?{' '}
              <button onClick={onUnitPurchase ?? onUpgrade} className="underline underline-offset-2 hover:text-violet-600 dark:hover:text-violet-400 shader:hover:text-violet-300 transition-colors">
                Achetez un bundle dès 29&nbsp;€, sans abonnement&nbsp;→
              </button>
            </p>
          ) : (
            <div className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10 border border-violet-200 dark:border-violet-700/50 shader:border-violet-400/20 max-w-md mx-auto">
              <svg className="w-4 h-4 text-violet-500 dark:text-violet-400 shader:text-violet-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
              </svg>
              <span className="text-sm text-violet-700 dark:text-violet-300 shader:text-violet-200">
                Pas prêt pour un abonnement ?{' '}
                <button onClick={onUnitPurchase ?? onUpgrade} className="font-semibold underline underline-offset-2 hover:text-violet-900 dark:hover:text-violet-100 transition-colors">
                  Achetez un bundle dès 29&nbsp;€
                </button>
              </span>
            </div>
          )
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
        {visibleOptions.map((option) => {
          const access = accessFor(option.key);
          const locked = isLocked(option.key);
          const noCredit = access.kind === 'no_credit';
          return (
            <button
              key={option.key}
              title={noCredit ? 'Plus de crédit pour ce type d\'analyse : acheter des crédits' : undefined}
              onClick={() => {
                if (noCredit) {
                  (onUnitPurchase ?? onUpgrade)?.();
                } else if (locked && onUpgrade) {
                  onUpgrade();
                } else if (!locked) {
                  onSelect(option);
                }
              }}
              onMouseMove={trackPointer}
              onMouseLeave={resetPointer}
              /* Mode pastel : la tuile prend l'aplat de sa typologie (`p-tile`,
                 variables --tile / --tile-strong) — le repère de couleur qui la
                 suit ensuite sur l'écran d'analyse. */
              style={{ '--tile': `var(--p-${tintSlot(option.key)})`, '--tile-strong': `var(--p-${tintSlot(option.key)}-strong)` } as React.CSSProperties}
              className={`glow-card group flex flex-col text-left p-6 rounded-xl pastel:rounded-3xl border transition-all duration-300 ${
                locked
                  ? `p-tile-locked border-slate-200 dark:border-slate-700 shader:border-white/10 bg-slate-50 dark:bg-slate-900/50 shader:bg-white/3 ${noCredit ? 'opacity-80 cursor-pointer' : 'opacity-60 cursor-default'}`
                  : 'p-tile border-slate-200 dark:border-slate-700 shader:border-white/10 bg-white dark:bg-slate-900 shader:bg-white/5 shader:backdrop-blur-xl hover:border-transparent'
              }`}
            >
              <div className="flex items-start justify-between pastel:mb-4">
                {/* Mode pastel : pastille d'icône blanche, le motif rond de la charte */}
                <span className="pastel:grid pastel:h-11 pastel:w-11 pastel:place-items-center pastel:rounded-full pastel:bg-white/60">
                  <AnalysisIcon
                    type={option.key}
                    className="w-7 h-7 mb-3 pastel:w-5 pastel:h-5 pastel:mb-0 text-slate-400 dark:text-white/50 pastel:text-(--p-ink) group-hover:text-slate-600 dark:group-hover:text-white/80 pastel:group-hover:text-(--p-ink) transition-colors duration-300"
                  />
                </span>
                {noCredit ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 shader:text-violet-300 pastel:text-white bg-violet-50 dark:bg-violet-900/30 shader:bg-violet-500/10 pastel:bg-(--p-ink) px-2 py-0.5 pastel:px-2.5 pastel:py-1 rounded-full">
                    0 crédit
                  </span>
                ) : locked ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 shader:text-violet-300 pastel:text-white bg-violet-50 dark:bg-violet-900/30 shader:bg-violet-500/10 pastel:bg-(--p-ink) px-2 py-0.5 pastel:px-2.5 pastel:py-1 rounded-full">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    {requiredPlanFor(option.key)}
                  </span>
                ) : access.kind === 'credits' ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 shader:text-emerald-300 pastel:text-(--p-ink) bg-emerald-50 dark:bg-emerald-900/30 shader:bg-emerald-500/10 pastel:bg-white/70 px-2 py-0.5 pastel:px-2.5 pastel:py-1 rounded-full">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {creditCountLabel(access.count)}
                  </span>
                ) : null}
              </div>
              <h3 className="font-display text-slate-900 dark:text-white pastel:text-(--p-ink) pastel:text-[1.05rem] mb-1 pastel:mb-1.5 transition-colors duration-300">
                {option.label}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/40 pastel:text-(--p-ink)/65 pastel:leading-relaxed group-hover:text-slate-700 dark:group-hover:text-white pastel:group-hover:text-(--p-ink) transition-colors duration-300">
                {option.description}
              </p>
              {noCredit ? (
                <div className="mt-auto pt-3 pastel:pt-4 text-xs font-semibold text-violet-600 dark:text-violet-400 shader:text-violet-300 pastel:text-(--p-ink) inline-flex items-center gap-1">
                  Acheter des crédits
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              ) : (
                <div className="mt-auto pt-3 pastel:pt-4 text-xs text-slate-400 dark:text-slate-500 shader:text-white/30 pastel:text-(--p-ink)/45">
                  {option.inputType === 'text' ? 'Saisie texte' : option.inputType === 'text+image' ? 'Texte + images' : 'Upload image(s)'}
                </div>
              )}
            </button>
          );
        })}

        {/* Bloc sur mesure — CTA non-analyse : ouvre un mail pré-rempli pour
            commander un outil/feature personnalisé (Loi Évin ou non). */}
        <button
          onClick={() => {
            const subject = 'Demande de bloc sur mesure — Evinly';
            const body = [
              'Bonjour,',
              '',
              'Je souhaite créer un bloc / outil sur mesure pour mes besoins.',
              '',
              'Type d\'outil envisagé (checker d\'étiquette, chatbot, automatisation IA, autre) :',
              'Description du besoin :',
              'Filière / secteur :',
              '',
              'Merci !',
            ].join('\n');
            window.location.href = `mailto:ludovic@blackthornsdesign.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          }}
          onMouseMove={trackPointer}
          onMouseLeave={resetPointer}
          className="glow-card p-tile-ink group flex flex-col text-left p-6 rounded-xl pastel:rounded-3xl border border-dashed pastel:border-solid border-violet-300 dark:border-violet-600/50 shader:border-violet-400/30 bg-violet-50/40 dark:bg-violet-900/10 shader:bg-violet-500/5 shader:backdrop-blur-xl hover:border-transparent transition-all duration-300"
        >
          <div className="flex items-start justify-between pastel:mb-4">
            <svg className="w-7 h-7 mb-3 pastel:w-5 pastel:h-5 pastel:mb-0 text-violet-400 dark:text-violet-400/70 pastel:text-(--p-b-sig) group-hover:text-violet-600 dark:group-hover:text-violet-300 pastel:group-hover:text-(--p-b-sig) transition-colors duration-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
            </svg>
          </div>
          <h3 className="font-display text-slate-900 dark:text-white pastel:text-white pastel:text-[1.05rem] mb-1 pastel:mb-1.5 transition-colors duration-300">
            Créez votre bloc sur mesure
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/40 pastel:text-white/60 pastel:leading-relaxed group-hover:text-slate-700 dark:group-hover:text-white pastel:group-hover:text-white/80 transition-colors duration-300">
            Besoin d'un outil sur mesure, Loi Évin ou non ? Checker d'étiquette, chatbot, automatisation IA… Nous le construisons pour vous.
          </p>
          <div className="mt-auto pt-3 pastel:pt-4 text-xs font-semibold text-violet-600 dark:text-violet-400 shader:text-violet-300 pastel:text-(--p-b-sig) inline-flex items-center gap-1">
            Contactez-nous
            <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
}
